import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { verifyCertificate, getCertProviderModes } from "@/lib/cert-verify.functions";
import {
  CERT_PROVIDER_SLUGS,
  CERT_PROVIDER_LABEL,
  isManualPath,
  type CertProvider,
  type NormalizedCertResult,
} from "@/lib/grading/cert-verification";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Info, ExternalLink, Loader2 } from "lucide-react";
import { GRADER_SHORT } from "@/lib/grading/companies";

type Company = CertProvider;

export type CertLookupResult = {
  grade: string | null;
  company: Company;
  cert_number: string;
  card_name: string | null;
  set_name: string | null;
  card_year: number | null;
  /** Only true for an official-source match. */
  verified: boolean;
  official_url: string;
  verified_at: string | null;
  method: NormalizedCertResult["method"];
};

function formatVerifiedAt(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

const METHOD_LABEL: Record<NormalizedCertResult["method"], string> = {
  official_partner_api: "official partner API",
  official_public_api: "official public API",
  official_lookup_endpoint: "official lookup endpoint",
  official_page: "official certificate page",
  manual_official_link: "manual official link",
  none: "none",
};

const PROBLEM_TITLE: Partial<Record<NormalizedCertResult["status"], string>> = {
  not_found: "Certificate not found",
  rate_limited: "Verification limit reached",
  manual_official_link: "Confirm on the official site",
  malformed: "Certificate number not recognised",
  temporarily_unavailable: "Verification temporarily unavailable",
  upstream_blocked: "Automated verification blocked by the grading company",
  parser_failed: "Official record could not be read",
  manual_review_required: "Confirm on the official site",
  unsupported: "Verification not available",
};

export function GradingCertLookup({
  onResult,
  initialCompany,
  initialCert,
  compact = false,
}: {
  onResult?: (r: CertLookupResult) => void;
  initialCompany?: Company;
  initialCert?: string;
  compact?: boolean;
}) {
  const verify = useServerFn(verifyCertificate);
  const modesFn = useServerFn(getCertProviderModes);
  const [company, setCompany] = useState<Company>(initialCompany ?? "psa");
  const [cert, setCert] = useState(initialCert ?? "");
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [res, setRes] = useState<NormalizedCertResult | null>(null);

  const modeQ = useQuery({
    queryKey: ["cert-provider-modes"],
    queryFn: () => modesFn(),
    staleTime: 5 * 60_000,
  });
  const automated = modeQ.data?.[company]?.automated ?? false;

  async function run() {
    // Duplicate-click guard: one lookup at a time.
    if (busy || !cert.trim()) return;
    setBusy(true);
    setRetrying(false);
    setRes(null);
    try {
      let r = await verify({ data: { provider: company, cert_number: cert.trim() } });
      // One client-side retry for transient upstream conditions only. Never for
      // malformed input, not-found records, or unauthorised automated access.
      if (r.status === "temporarily_unavailable") {
        setRetrying(true);
        await new Promise((res2) => setTimeout(res2, 2000));
        r = await verify({ data: { provider: company, cert_number: cert.trim() } });
        setRetrying(false);
      }
      setRes(r);
      if (r.verified) {
        toast.success(
          `Verified against the official ${CERT_PROVIDER_LABEL[company]} certificate record.`,
        );
        onResult?.({
          grade: r.grade,
          company,
          cert_number: r.certificateNumber,
          card_name: r.cardName,
          set_name: r.setName,
          card_year: r.year,
          verified: true,
          official_url: r.officialUrl,
          verified_at: r.verifiedAt,
          method: r.method,
        });
      } else if (r.status === "rate_limited") {
        toast(PROBLEM_TITLE.rate_limited, {
          description: r.retryAfterSeconds
            ? `Please wait about ${r.retryAfterSeconds} seconds and try again.`
            : r.message,
        });
      } else {
        toast(PROBLEM_TITLE[r.status] ?? "Verification unavailable", { description: r.message });
      }
    } catch {
      toast.error("Certificate lookup could not be completed", {
        description: "Please enter the grading details manually and try again later.",
      });
    } finally {
      setRetrying(false);
      setBusy(false);
    }
  }

  const problem = res && !res.verified;

  return (
    <div className={compact ? "space-y-2" : "space-y-2 rounded-md border p-3"}>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={company}
          onValueChange={(v) => {
            setCompany(v as Company);
            setRes(null);
          }}
        >
          <SelectTrigger className="w-28" aria-label="Grading company">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CERT_PROVIDER_SLUGS.map((g) => (
              <SelectItem key={g} value={g}>
                {GRADER_SHORT[g.toUpperCase() as keyof typeof GRADER_SHORT] ?? g.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={cert}
          onChange={(e) => setCert(e.target.value)}
          placeholder="Cert number"
          aria-label="Certificate number"
          inputMode="text"
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void run();
            }
          }}
          className="flex-1 min-w-[10rem]"
        />
        <Button onClick={run} disabled={busy || !cert.trim()} aria-busy={busy} className="min-h-11">
          {busy ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Checking…
            </span>
          ) : (
            "Verify"
          )}
        </Button>

        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
            automated
              ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
              : "border border-amber-500/30 bg-amber-500/10 text-amber-400"
          }`}
          title={
            automated
              ? "Automated verification enabled — BidNova checks the official record directly"
              : "Manual official verification — confirm the record on the grading company's site"
          }
        >
          {automated ? (
            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
          ) : (
            <Info className="h-3 w-3" aria-hidden="true" />
          )}
          {automated ? "Automated verification enabled" : "Manual official verification"}
        </span>
      </div>

      {busy && (
        <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
          {retrying
            ? "Retrying official certificate verification…"
            : "Checking the official certificate record…"}
        </p>
      )}

      {res?.verified && (
        <div
          className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm"
          role="status"
          aria-live="polite"
        >
          <p className="inline-flex items-center gap-1.5 font-medium text-emerald-200">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Verified against the official {CERT_PROVIDER_LABEL[res.provider]} certificate record.
          </p>
          <p className="mt-1 text-sm text-foreground">
            {res.cardName ?? "Certificate"} — grade {res.grade ?? "—"} · cert #
            {res.certificateNumber}
          </p>
          <p className="text-xs text-muted-foreground">
            {[res.year, res.setName, res.cardNumber, res.variant].filter(Boolean).join(" · ")}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Verified {formatVerifiedAt(res.verifiedAt)}
            {res.cached ? " (cached)" : ""} · method: {METHOD_LABEL[res.method]}
          </p>
          <a
            href={res.officialUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-300 underline underline-offset-2"
          >
            View official certificate <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
      )}

      {problem && (
        <div
          className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-100"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-medium text-amber-50">
                {PROBLEM_TITLE[res.status] ?? "Verification unavailable"}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-amber-200/90">{res.message}</p>
              {res.status === "rate_limited" && res.retryAfterSeconds ? (
                <p className="mt-1 text-xs text-amber-200/90">
                  Try again in about {res.retryAfterSeconds} seconds.
                </p>
              ) : null}
              {res.status !== "malformed" && res.status !== "rate_limited" && (
                <a
                  href={res.officialUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-md border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-100 hover:bg-amber-500/20"
                >
                  Open official certificate page{" "}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              )}
              <p className="mt-2 text-xs text-amber-200/80">
                You can still enter the grading details manually
                {isManualPath(res.status) ? " and continue listing" : ""}. Nothing is prefilled from
                an unverified source.
              </p>
              <p className="mt-2 inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-300">
                {res.status === "temporarily_unavailable" || res.status === "rate_limited"
                  ? "Verification pending"
                  : "Not independently verified"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
